import { useState, useEffect, useRef, createElement } from 'react';
import { Grid, Typography, InputBase, CardActionArea, CircularProgress, useTheme } from '@mui/material';
import { ColorPicker } from '../../../../../Global/Components';
import { useHistory } from 'react-router-dom';
import StateManager from '../../../../../Global/StateManager';
import axios from 'axios';
import * as Icons from '@mui/icons-material';
import MenuList from '../MenuList';
import ACTION_TYPES from './ActionTypes';
import ImageSizeDialog from './ImageSizeDialog';

export default function ActionItem({ editable, item, onDelete, onChange, portalSpaceId }) {
  const [loading, setLoading] = useState(false);
  const [colorPicker, setColorPicker] = useState(null);
  const [color, setColor] = useState(undefined);
  const [text, setText] = useState(null);
  const [image, setImage] = useState(null);
  const [icon, setIcon] = useState({ icon: 'ImageSearch' });
  const [fontSettings, setFontSettings] = useState({});
  const [uploadingImage, setUploadingImage] = useState(false);
  const [height, setHeight] = useState(null);
  const [sizeDialog, setSizeDialog] = useState(false);
  const [imageSize, setImageSize] = useState('contain');
  const theme = useTheme();
  const history = useHistory();
  const titleTimer = useRef(null);
  const elementRef = useRef(null);

  useEffect(() => {
    setColor(item?.color);
    setText(item?.text || null);
    setImage(item?.image || null);
    setFontSettings(item?.fontSettings || null);
    setIcon(item?.icon || { icon: 'ImageSearch' });
    setImageSize(item?.imageSize || 'contain');
  }, [item]);

  useEffect(() => {
    const updateSize = () => {
      if (elementRef.current) {
        const rect = elementRef.current.getBoundingClientRect();
        setHeight(rect.height);
      }
    };

    updateSize(); // Initial update
    window.addEventListener('resize', updateSize); // Update on resize

    return () => {
      window.removeEventListener('resize', updateSize);
    };
  }, []);

  function saveColor(col) {
    setColor(col);
    onChange({ color: col });
  }

  function saveIcon(icon) {
    setIcon(icon);
    onChange({ icon: icon });
  }

  function saveTitle(title) {
    setText(title);
    if (titleTimer.current != null) {
      clearTimeout(titleTimer.current);
    }
    titleTimer.current = setTimeout(() => onChange({ text: title }), 1000);
  }

  function saveFontSettings(settings) {
    setFontSettings(settings);
    onChange({ fontSettings: settings });
  }

  function saveImageSize(size) {
    setImageSize(size);
    onChange({ imageSize: size });
  }

  function createTaskFromTemplate(templateId) {
    setLoading(true);
    axios
      .get('/tasks/templates/getTaskTemplate', { params: { templateId } })
      .then((res) => {
        StateManager.setNewTask({ template: res.data }, (task) => StateManager.selectTask(task._id));
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        StateManager.setAxiosErrorAlert(err);
      });
  }

  function onClick() {
    if (item.type === 'taskTemplate') {
      createTaskFromTemplate(item.id);
    }
    if (item.type === 'form') {
      history.push(`/forms/${item.id}/new-entry${portalSpaceId ? `?portalSpaceId=${portalSpaceId}` : ''}`);
    }
    if (item.type === 'process') {
      history.push(`/processes/start-process/${item.id}${portalSpaceId ? `?portalSpaceId=${portalSpaceId}` : ''}`);
    }
    if (item.type === 'register') {
      history.push(`/registers/${item.id}?newRow=true`);
    }
  }

  function uploadFile(file) {
    setUploadingImage(true);
    const data = new FormData();
    data.append('file', file);
    axios
      .post(`/uploader/upload-single`, data, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then(({ data }) => {
        setImage(data.file);
        onChange({ image: data.file }, { h: 8 });
        setUploadingImage(false);
      })
      .catch((err) => {
        StateManager.setAxiosErrorAlert(err);
        setUploadingImage(false);
      });
  }

  function handleOpenSize() {
    setSizeDialog(true);
  }
  return (
    <Grid
      container
      direction={'column'}
      style={{
        height: '100%',
        padding: 0,
        background: image ? `url('${image.location}') center center / ${imageSize} no-repeat` : color,
      }}
      wrap="nowrap"
      ref={elementRef}
    >
      {editable ? (
        <>
          <Grid
            container
            item
            alignItems="center"
            wrap="nowrap"
            style={{
              background: image ? `rgba(0,0,0, .5)` : 'inherit',
              maxHeight: 'fit-content',
              position: 'absolute',
              bottom: 0,
              paddingBottom: '5px',
              paddingLeft: '10px',
            }}
          >
            {icon && icon.icon !== 'ImageSearch'
              ? createElement(Icons[icon.icon], {
                  style: {
                    color: icon?.color
                      ? icon?.color
                      : image
                      ? '#ffffff'
                      : color
                      ? theme.palette.getContrastText(color)
                      : theme.palette.secondary,
                    mx: 2,
                  },
                })
              : ACTION_TYPES[item.type]?.icon}
            <InputBase
              style={{ marginLeft: 8 }}
              inputProps={{
                style: { fontSize: fontSettings?.size || 18, textAlign: fontSettings?.alignment ?? 'left' },
              }}
              value={text != null ? text : `${ACTION_TYPES[item.type]?.text} ${item.title}`}
              onChange={(e) => saveTitle(e.target.value)}
              className="non-draggable"
              sx={{
                width: '100%',
                color: image ? '#FFFFFF' : color ? theme.palette.getContrastText(color) : null,
                fontSize: fontSettings?.size || 18,
              }}
            />
            <ColorPicker
              anchor={colorPicker}
              open={Boolean(colorPicker)}
              column={400}
              customPicker
              onClose={(res) => {
                if (res?.color) {
                  saveColor(res?.color);
                }
                setColorPicker(null);
              }}
              className="non-draggable"
            />
            <MenuList
              image={image}
              item={item}
              uploadFile={uploadFile}
              uploadingImage={uploadingImage}
              onChange={onChange}
              color={color}
              setColorPicker={setColorPicker}
              icon={icon}
              saveIcon={saveIcon}
              onDelete={onDelete}
              fontSettings={fontSettings}
              saveFontSettings={saveFontSettings}
              mode={'action'}
              setSizeDialog={handleOpenSize}
            />
          </Grid>
          <ImageSizeDialog
            open={sizeDialog}
            onClose={() => setSizeDialog(false)}
            onChange={(value) => saveImageSize(value)}
          />
        </>
      ) : (
        <Grid
          container
          component={CardActionArea}
          onClick={onClick}
          justifyContent={image || height > 100 ? 'end' : 'center'}
          style={{
            padding: 0,
            height: '100%',
            width: '100%',
            borderRadius: 5,
            overflow: 'hidden',
            transition: 'background-color 0.3s ease',
            cursor: 'pointer',
          }}
          wrap="nowrap"
          direction="column"
        >
          {height > 100 && !item?.image && (
            <Grid container item justifyContent={'center'} maxHeight={'calc(100% - 50px)'} alignItems={'center'}>
              {item.icon &&
                icon.icon !== 'ImageSearch' &&
                !image &&
                createElement(Icons[item?.icon?.icon], {
                  style: {
                    color: item?.icon?.color ?? '#ffffff',
                    height: '50%',
                    width: '50%',
                    alignSelf: 'center',
                    justifySelf: 'center',
                    flexGrow: 1,
                  },
                })}
              {!item.icon && ACTION_TYPES[`${item.type}Big`]?.icon}
            </Grid>
          )}
          <Grid
            container
            alignItems="center"
            wrap="nowrap"
            justifyContent={fontSettings?.alignment ? fontSettings.alignment : 'center'}
            width="100%"
            style={{
              background: image ? `rgba(0,0,0, .5)` : 'inherit',
              maxHeight: 'fit-content',
              paddingLeft: '10px',
              paddingBottom: '5px',
              paddingTop: '5px',
            }}
          >
            {loading ? (
              <CircularProgress size={24} />
            ) : item.icon && (height <= 100 || image) ? (
              createElement(Icons[item?.icon?.icon], {
                style: {
                  color: item?.icon?.color
                    ? item?.icon?.color
                    : image
                    ? '#ffffff'
                    : color
                    ? theme.palette.getContrastText(color)
                    : theme.palette.secondary,
                },
                mx: 2,
              })
            ) : height <= 100 || image ? (
              ACTION_TYPES[item.type]?.icon
            ) : null}
            <Typography
              noWrap
              style={{
                color: image ? '#FFFFFF' : color ? theme.palette.getContrastText(color) : null,
                marginLeft: 16,
                marginRight: 16,
                fontSize: fontSettings?.size ? fontSettings.size : 18,
              }}
            >
              {item.text || `${ACTION_TYPES[item.type]?.text} ${item.title}`}
            </Typography>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
}
